import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SelectAuto,
  Textarea,
} from '@hero/web-components';
import type { FinancingLenderView } from '@hero/api';
import type {
  FinancingDeclineReason,
  FinancingOfferTerms,
  TermDuration,
} from '@hero/schema';

const TERM_OPTIONS: { value: string; label: string }[] = [
  { value: '30_days', label: '30 days' },
  { value: '60_days', label: '60 days' },
  { value: '90_days', label: '90 days' },
  { value: '6_months', label: '6 months' },
  { value: '1_year', label: '1 year' },
];

const DECLINE_REASONS: FinancingDeclineReason[] = [
  'risk_too_high',
  'incomplete_profile',
  'customer_concentration',
  'outside_criteria',
  'other',
];

function termFromKey(key: string): TermDuration {
  if (key === '30_days') return { value: 30, type: 'day' };
  if (key === '60_days') return { value: 60, type: 'day' };
  if (key === '90_days') return { value: 90, type: 'day' };
  if (key === '6_months') return { value: 6, type: 'month' };
  return { value: 12, type: 'month' };
}

function keyFromTerm(term: TermDuration): string {
  if (term.type === 'day' && term.value === 30) return '30_days';
  if (term.type === 'day' && term.value === 60) return '60_days';
  if (term.type === 'day' && term.value === 90) return '90_days';
  if (term.type === 'month' && term.value === 6) return '6_months';
  return '1_year';
}

export type LenderViewActionsProps = {
  view: FinancingLenderView;
  onExpressInterest: () => Promise<void>;
  onSubmitOffer: (terms: FinancingOfferTerms) => Promise<void>;
  onDecline: (input: {
    reason: FinancingDeclineReason;
    notes?: string;
  }) => Promise<void>;
};

/**
 * Three-state action surface for the lender-view page.
 *
 *  - `decision='pending'`     → "I'm interested" + "Decline" (modal)
 *  - `decision='interested'`  → offer form + "Decline" (same modal)
 *  - `decision='offered'`     → read-only "offer sent" banner
 *  - `decision='declined'`    → read-only "you declined" banner
 *  - `decision='accepted'`    → read-only "carrier picked you" banner
 *
 * Override — `application.acceptedByOtherLender === true`
 * short-circuits all of the above (except the winner's own
 * `'accepted'` state) and renders a neutral "another lender was
 * picked" info box. This applies to every non-winning state
 * (`pending` / `interested` / `offered`) and replaces the green
 * "we received your offer" / "I'm interested" panels so a
 * runner-up never wastes time acting on a deal that's already
 * closed. `declined` stays as-is — the lender already opted out.
 *
 * Decline UX — single pattern on both stages: the "Decline" button
 * opens {@link DeclineModal} asking for a reason (required) plus
 * optional notes. Keeping one path means the lender sees the same
 * affordance whether they bounce early from the masked packet or
 * after unmasking and reviewing details. Quick decline from
 * `pending` is allowed by the backend (`lenderSubmitOfferAPI` only
 * gates `interestedAt` on `decision: 'offered'`), so we never
 * force a lender to express interest just to record a reasoned no.
 *
 * Mirrors the legacy `PartnerActions` UX so lenders moving between
 * the deprecated `/financing/partner/:appId` link and the new
 * `/financing-application/:appId` link see no change in flow.
 */
export function LenderViewActions({
  view,
  onExpressInterest,
  onSubmitOffer,
  onDecline,
}: LenderViewActionsProps) {
  const { t } = useTranslation('m-financing');
  const { application } = view;
  const { myOffer, requested } = application;

  /**
   * Runner-up override — once another lender wins, every
   * non-winning state collapses into the same neutral info box.
   * The winner themselves reads `decision === 'accepted'` and is
   * exempt; declined lenders keep their gray closeout because they
   * already chose to walk away and the new banner would only
   * confuse them.
   */
  if (
    application.acceptedByOtherLender &&
    myOffer.decision !== 'accepted' &&
    myOffer.decision !== 'declined'
  ) {
    return <AcceptedByOtherLenderState />;
  }

  if (
    myOffer.decision === 'offered' ||
    myOffer.decision === 'declined' ||
    myOffer.decision === 'accepted'
  ) {
    return <ReadOnlyState decision={myOffer.decision} />;
  }

  if (myOffer.decision === 'pending') {
    return (
      <InterestPrompt
        onInterested={onExpressInterest}
        onDecline={async input => {
          try {
            await onDecline(input);
            toast.success(t('partner_page.toasts.decline_success'));
          } catch (err) {
            toast.error(
              err instanceof Error
                ? err.message
                : t('partner_page.toasts.submit_failed'),
            );
            throw err;
          }
        }}
      />
    );
  }

  return (
    <OfferForm
      requestedAmount={requested.amount}
      requestedTerm={requested.term}
      currency={application.invoice.currency}
      onSubmit={async terms => {
        try {
          await onSubmitOffer(terms);
          toast.success(t('partner_page.toasts.offer_success'));
        } catch (err) {
          toast.error(
            err instanceof Error
              ? err.message
              : t('partner_page.toasts.submit_failed'),
          );
        }
      }}
      onDecline={async input => {
        try {
          await onDecline(input);
          toast.success(t('partner_page.toasts.decline_success'));
        } catch (err) {
          toast.error(
            err instanceof Error
              ? err.message
              : t('partner_page.toasts.submit_failed'),
          );
          throw err;
        }
      }}
    />
  );
}

function InterestPrompt({
  onInterested,
  onDecline,
}: {
  onInterested: () => Promise<void>;
  onDecline: (input: {
    reason: FinancingDeclineReason;
    notes?: string;
  }) => Promise<void>;
}) {
  const { t } = useTranslation('m-financing');
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
      <h2 className="text-sm font-semibold text-gray-900">
        {t('partner_page.actions.interested')}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {t('partner_page.masked.description')}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={interestSubmitting || declineOpen}
          onClick={async () => {
            setInterestSubmitting(true);
            try {
              await onInterested();
            } finally {
              setInterestSubmitting(false);
            }
          }}>
          {interestSubmitting
            ? t('partner_page.actions.interested_submitting')
            : t('partner_page.actions.interested')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={interestSubmitting || declineOpen}
          onClick={() => setDeclineOpen(true)}>
          {t('partner_page.actions.decline')}
        </Button>
      </div>
      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={onDecline}
      />
    </section>
  );
}

/**
 * Reason-required dialog for the quick-decline path on the masked
 * pending state. Reuses the existing `reject_modal.*` translations
 * so copy stays consistent with the in-form decline section below
 * the offer fields. Submits through the parent's `onDecline` and
 * closes itself on success; errors bubble up so the parent's toast
 * fires and we keep the dialog open for a retry.
 */
function DeclineModal({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: {
    reason: FinancingDeclineReason;
    notes?: string;
  }) => Promise<void>;
}) {
  const { t } = useTranslation('m-financing');
  const [reason, setReason] = useState<FinancingDeclineReason>('outside_criteria');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason('outside_criteria');
    setNotes('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        reason,
        notes: notes.trim() ? notes.trim() : undefined,
      });
      reset();
      onClose();
    } catch {
      // Parent already toasted the error; keep the dialog open so
      // the lender can correct the reason / notes and retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !submitting) {
          reset();
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('reject_modal.title')}</DialogTitle>
          <DialogDescription>{t('reject_modal.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <SelectAuto
            label={t('reject_modal.fields.reason.title')}
            placeholder={t('reject_modal.fields.reason.placeholder')}
            value={reason}
            onChange={(value: string) =>
              setReason(value as FinancingDeclineReason)
            }
            options={DECLINE_REASONS.map(r => ({
              value: r,
              label: t(`decline_reason.${r}`),
            }))}
          />
          <div className="flex flex-col gap-1.5">
            <Label>{t('reject_modal.fields.notes.title')}</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('reject_modal.fields.notes.placeholder')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => {
              reset();
              onClose();
            }}>
            {t('reject_modal.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={handleSubmit}>
            {submitting
              ? t('reject_modal.submitting')
              : t('reject_modal.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfferForm({
  requestedAmount,
  requestedTerm,
  currency,
  onSubmit,
  onDecline,
}: {
  requestedAmount: number;
  requestedTerm: TermDuration;
  currency: string;
  onSubmit: (terms: FinancingOfferTerms) => Promise<void>;
  onDecline: (input: {
    reason: FinancingDeclineReason;
    notes?: string;
  }) => Promise<void>;
}) {
  const { t } = useTranslation('m-financing');
  const [advancedAmount, setAdvancedAmount] = useState(String(requestedAmount));
  const [termKey, setTermKey] = useState<string>(keyFromTerm(requestedTerm));
  const [feeAmount, setFeeAmount] = useState('');
  const [feePercentage, setFeePercentage] = useState('');
  const [repaymentDate, setRepaymentDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState('');
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  /**
   * Fee percentage is computed against the carrier's **requested
   * amount**, not the advance the lender is putting up. This
   * matches how invoice-factoring fees are conventionally quoted
   * (a percentage of the underlying invoice the carrier is
   * factoring) and keeps the rate stable when the lender adjusts
   * the advance up or down — the carrier shopping multiple offers
   * can compare rates on a like-for-like basis.
   */
  const onAdvancedChange = (raw: string) => {
    setAdvancedAmount(raw);
  };
  const onFeeAmountChange = (raw: string) => {
    setFeeAmount(raw);
    const fee = Number(raw);
    if (
      requestedAmount > 0 &&
      Number.isFinite(fee) &&
      fee >= 0
    ) {
      setFeePercentage(((fee / requestedAmount) * 100).toFixed(2));
    }
  };
  const onFeePercentChange = (raw: string) => {
    setFeePercentage(raw);
    const pct = Number(raw);
    if (requestedAmount > 0 && Number.isFinite(pct) && pct >= 0) {
      setFeeAmount(((requestedAmount * pct) / 100).toFixed(0));
    }
  };

  const feeAmt = Number(feeAmount);
  const feePct = Number(feePercentage);
  const advanceNum = Number(advancedAmount);
  /**
   * Invoice-factoring constraint — the advance can never exceed the
   * carrier's requested amount. Surfaced as an inline error here so
   * the lender corrects it before submitting; the backend enforces
   * the same rule via `financing_advance_exceeds_requested` for
   * defence in depth.
   */
  const isAdvanceWithinRequested = advanceNum <= requestedAmount;
  const isOfferValid =
    advanceNum > 0 &&
    isAdvanceWithinRequested &&
    Number.isFinite(feeAmt) &&
    feeAmt > 0 &&
    Number.isFinite(feePct) &&
    feePct > 0 &&
    Boolean(termKey) &&
    Boolean(repaymentDate);

  const handleSubmitOffer = async () => {
    if (!isOfferValid || !repaymentDate) return;
    setSubmittingOffer(true);
    try {
      await onSubmit({
        advancedAmount: Number(advancedAmount),
        feeAmount: feeAmt,
        feePercentage: feePct / 100,
        term: termFromKey(termKey),
        repaymentDate,
        recourse: true,
        notes: notes.trim() ? notes.trim() : undefined,
      });
    } finally {
      setSubmittingOffer(false);
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
      <h2 className="text-sm font-semibold text-gray-900">
        {t('partner_page.actions.submit_offer')}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {t('partner_page.states.interested_only.description')}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>{t('accept_drawer.fields.advanced_amount.title')}</Label>
          <Input
            type="number"
            min={0}
            max={requestedAmount}
            value={advancedAmount}
            onChange={e => onAdvancedChange(e.target.value)}
            placeholder={`SAR (requested ${requestedAmount.toLocaleString()})`}
          />
          <p className="text-xs text-gray-500">
            {t('partner_page.fields.requested_amount_hint', {
              amount: `${currency} ${requestedAmount.toLocaleString()}`,
            })}
          </p>
          {advanceNum > 0 && !isAdvanceWithinRequested && (
            <p className="text-xs font-medium text-red-600">
              {t('partner_page.fields.advance_exceeds_requested')}
            </p>
          )}
        </div>
        <SelectAuto
          label={t('accept_drawer.fields.term.title')}
          placeholder={t('accept_drawer.fields.term.placeholder')}
          value={termKey}
          onChange={setTermKey}
          options={TERM_OPTIONS.map(opt => ({
            value: opt.value,
            label: t(`term.${opt.value}` as never),
          }))}
        />
        <div className="flex flex-col gap-1.5">
          <Label>{t('accept_drawer.fields.fee_amount.title')}</Label>
          <Input
            type="number"
            min={0}
            value={feeAmount}
            onChange={e => onFeeAmountChange(e.target.value)}
            placeholder={t('accept_drawer.fields.fee_amount.placeholder')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('accept_drawer.fields.fee_percentage.title')}</Label>
          <Input
            type="number"
            min={0}
            value={feePercentage}
            onChange={e => onFeePercentChange(e.target.value)}
            placeholder={t('accept_drawer.fields.fee_percentage.placeholder')}
          />
          <p className="text-xs text-gray-500">
            {t('partner_page.fields.fee_percentage_hint', {
              amount: `${currency} ${requestedAmount.toLocaleString()}`,
            })}
          </p>
        </div>
        <DateField
          label={t('accept_drawer.fields.repayment_date.title')}
          required
          value={repaymentDate}
          onChange={setRepaymentDate}
        />
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>{t('accept_drawer.fields.notes.title')}</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('accept_drawer.fields.notes.placeholder')}
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>
          {t('partner_page.fields.invoice_amount')}:{' '}
          <span className="font-mono">
            {currency} {requestedAmount.toLocaleString()}
          </span>
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={submittingOffer || declineOpen || !isOfferValid}
          onClick={handleSubmitOffer}>
          {submittingOffer
            ? t('accept_drawer.submitting')
            : t('partner_page.actions.submit_offer')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={submittingOffer || declineOpen}
          onClick={() => setDeclineOpen(true)}>
          {t('partner_page.actions.decline')}
        </Button>
      </div>

      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={onDecline}
      />
    </section>
  );
}

/**
 * Neutral "another lender was picked" info box shown to every
 * runner-up (`pending` / `interested` / `offered`) once the
 * application is off the market. Deliberately small and muted —
 * not red (this isn't a rejection of THIS lender) and not green
 * (the winner gets the green state via `ReadOnlyState`). We use
 * indigo to set it apart from the gray "declined" closeout and the
 * amber rejection banner the carrier sees on a bounced receipt, so
 * the lender's pipeline reads three distinct end-states at a
 * glance.
 *
 * Carries no winner name, no winning terms — that's competitive
 * information we don't leak across the wall.
 */
function AcceptedByOtherLenderState() {
  const { t } = useTranslation('m-financing');
  return (
    <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-indigo-900">
        {t('partner_page.states.not_selected.title')}
      </h2>
      <p className="mt-1 text-sm text-indigo-800">
        {t('partner_page.states.not_selected.description')}
      </p>
    </section>
  );
}

function ReadOnlyState({
  decision,
}: {
  decision: FinancingLenderView['application']['myOffer']['decision'];
}) {
  const { t } = useTranslation('m-financing');
  /**
   * Translation keys still spell the "lender sent terms" state as
   * `approved` (the carrier-facing copy in `m-financing.json` is
   * intentionally not renamed — the carrier reads it as "the
   * lender approved your application"). The on-the-wire enum
   * value is `'offered'`; we map it to the legacy key here. The
   * new `'accepted'` state surfaces "the carrier picked you" — use
   * the same green panel as `offered`, with its own translation
   * subtree.
   */
  const config: { tone: 'green' | 'gray'; key: string } = (() => {
    if (decision === 'offered') return { tone: 'green', key: 'approved' };
    if (decision === 'accepted') return { tone: 'green', key: 'accepted' };
    return { tone: 'gray', key: 'declined' };
  })();
  return (
    <section
      className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${
        config.tone === 'green'
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-gray-200 bg-gray-50'
      }`}>
      <h2 className="text-sm font-semibold text-gray-900">
        {t(`partner_page.states.${config.key}.title`)}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {t(`partner_page.states.${config.key}.description`)}
      </p>
    </section>
  );
}
